import { useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Epic,
  Panel,
  PanelHeader,
  Tabbar,
  TabbarItem,
  HorizontalScroll,
  Tabs,
  TabsItem,
} from '@vkontakte/vkui';
import { Icon28UserOutline, Icon28SettingsOutline } from '@vkontakte/icons';

type NavItem = { path: string; label: string; adminOnly?: boolean };

type NavGroup = {
  id: string;
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
};

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    title: 'Обзор',
    items: [
      { path: 'dashboard', label: 'Дашборд' },
      { path: 'user-flow', label: 'Путь участника', adminOnly: true },
    ],
  },
  {
    id: 'data',
    title: 'Участники и данные',
    items: [
      { path: 'participants', label: 'Участники' },
      { path: 'staff', label: 'Администраторы', adminOnly: true },
      { path: 'licenses', label: 'Лицензии', adminOnly: true },
    ],
  },
  {
    id: 'content',
    title: 'Контент приложения',
    adminOnly: true,
    items: [
      { path: 'maps', label: 'Карты' },
      { path: 'simulator', label: 'Симулятор' },
    ],
  },
  {
    id: 'export',
    title: 'Экспорт',
    items: [{ path: 'export', label: 'Экспорт' }],
  },
];

function AdminNav({
  isAdmin,
  pathname,
  onNavigate,
}: {
  isAdmin: boolean;
  pathname: string;
  onNavigate: (path: string) => void;
}) {
  const navItems = useMemo(
    () =>
      NAV_GROUPS.filter((g) => !g.adminOnly || isAdmin).flatMap((g) =>
        g.items.filter((item) => !item.adminOnly || isAdmin),
      ),
    [isAdmin],
  );

  const selectedId =
    navItems.find((item) => {
      const segment = pathname.replace(/\/$/, '').split('/').pop();
      return segment === item.path;
    })?.path ?? navItems[0]?.path;

  return (
    <div className="pb-admin-nav">
      <HorizontalScroll showArrows={false} withPadding>
        <Tabs
          mode="secondary"
          layoutFillMode="shrinked"
          selectedId={selectedId}
          withScrollToSelectedTab
        >
          {navItems.map((item) => (
            <TabsItem
              key={item.path}
              id={item.path}
              selected={pathname.replace(/\/$/, '').split('/').pop() === item.path}
              onClick={() => onNavigate(item.path)}
            >
              {item.label}
            </TabsItem>
          ))}
        </Tabs>
      </HorizontalScroll>
    </div>
  );
}

export function AdminLayout({ role }: { role: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = role === 'admin';

  const tabbar = (
    <Tabbar>
      <TabbarItem label="Профиль" onClick={() => navigate('/profile')}>
        <Icon28UserOutline />
      </TabbarItem>
      <TabbarItem selected label="Админ">
        <Icon28SettingsOutline />
      </TabbarItem>
    </Tabbar>
  );

  return (
    <Epic activeStory="admin" tabbar={tabbar}>
      <Panel id="admin">
        <PanelHeader>Админ-панель</PanelHeader>
        <div className="pb-admin-content">
          <AdminNav
            isAdmin={isAdmin}
            pathname={location.pathname}
            onNavigate={(path) => navigate(`/admin/${path}`)}
          />
          <Outlet />
        </div>
      </Panel>
    </Epic>
  );
}
